import { useState } from 'react';
import BigNumber from 'bignumber.js';
import {
  CurrencyFormat,
  Dropdown,
  Icon,
  NoFound,
  Search,
  Spinner,
  Table,
  TableRow,
  Tabs,
} from '@/components/generic';
import { cn } from '@/lib/utils';
import { copyToClipboard, formatUnitsToAmount, shortenAddress } from '@/utils';
import { useGetOnchainUserTxs } from '@package/smart-wallet/hooks';
import type { TOnchainUserTx } from '@xhype/card-app-sdk';
import { useTranslation } from 'react-i18next';

// Transaction types with color classifications
const TRANSACTION_TYPES = {
  CREATE: { label: 'Create Card', color: 'label-blue', priority: 'high' },
  DEPOSIT: { label: 'Deposit', color: 'label-green', priority: 'high' },
  CANCEL: { label: 'Cancel Card', color: 'label-red', priority: 'high' },
  FREEZE: { label: 'Freeze Card', color: 'label-orange', priority: 'medium' },
  UNFREEZE: {
    label: 'Unfreeze Card',
    color: 'label-green',
    priority: 'medium',
  },
  AUTH: { label: 'Authorization', color: 'label-yellow', priority: 'medium' },
  CORRECT_AUTH: {
    label: 'Auth Correction',
    color: 'label-gray',
    priority: 'low',
  },
  CORRECT_REFUND: {
    label: 'Refund Correction',
    color: 'label-gray',
    priority: 'low',
  },
  REFUND: { label: 'Refund', color: 'label-purple', priority: 'high' },
  VERIFICATION: {
    label: 'Verification',
    color: 'label-blue',
    priority: 'medium',
  },
  VOID: { label: 'Void', color: 'label-red', priority: 'medium' },
  CARD_PATCH_FEE: {
    label: 'Fee Correction',
    color: 'label-gray',
    priority: 'low',
  },
  CARD_PATCH_CROSS_BORDER: {
    label: 'Cross-Border Fee',
    color: 'label-gray',
    priority: 'low',
  },
  CARD_PATCH_SETTLE: {
    label: 'Settlement Reversal',
    color: 'label-gray',
    priority: 'low',
  },
  THIRD_3DS_OTP: { label: '3DS OTP', color: 'label-blue', priority: 'medium' },
  HOLDER: { label: 'Card Holder', color: 'label-blue', priority: 'medium' },
  AUTH_URL: { label: 'Auth URL', color: 'label-blue', priority: 'low' },
  ONCHAIN_DEPOSIT: {
    label: 'On-Chain Deposit',
    color: 'label-green',
    priority: 'high',
  },
  CARD_ACTIVE: {
    label: 'Card Active',
    color: 'label-green',
    priority: 'medium',
  },
} as const;

const STATUS_COLORS = {
  PENDING: 'label-yellow',
  SUCCESS: 'label-green',
  COMPLETED: 'label-green',
  FAILED: 'label-red',
  CANCELLED: 'label-gray',
} as const;

const sortOptions = [
  { id: 1, name: 'All' },
  { id: 2, name: 'Pending' },
  { id: 3, name: 'Success' },
  { id: 4, name: 'Failed' },
  { id: 5, name: 'Cancelled' },
];

const tableHead = [
  'Tx ID',
  'Date',
  'Status',
  'Type',
  'Order No.',
  'Amount',
  'Description',
  '',
];

// Helper function to format date
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// ONCHAIN_DEPOSIT rows do not all store `amount` in the same unit: older rows
// hold the raw on-chain value (18 decimals) while rows written by the ONHOLD
// path hold the USDT amount already scaled down ("554.75000000"). Feeding the
// second shape to ethers threw `invalid BigNumberish string` mid-render and
// blanked the page, so mirror the backend's 1e12 disambiguation threshold
// (no real deposit reaches 1T USDT, no Wei amount is below 1e18).
const WEI_THRESHOLD = new BigNumber('1000000000000');

const getTransactionAmount = (transaction: TOnchainUserTx): number => {
  const raw = new BigNumber(String(transaction?.amount ?? '0').trim());
  if (!raw.isFinite()) return 0;

  if (transaction?.type !== 'ONCHAIN_DEPOSIT' || raw.abs().lt(WEI_THRESHOLD)) {
    return raw.toNumber();
  }

  return Number(
    formatUnitsToAmount({
      string: raw.integerValue(BigNumber.ROUND_DOWN).toFixed(0),
      decimalToken: 18,
    }),
  );
};
const TransactionsHistory = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(sortOptions[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  const {
    isLoading,
    records: transactionsData,
    total,
  } = useGetOnchainUserTxs({
    pageNum: currentPage,
    pageSize: pageSize,
  });
  // Filter transactions based on search and sort
  // `orderNo`, `status` and `type` are nullable in the API, so every string
  // access here is guarded: a null field used to throw during render.
  const searchTerm = search.toLowerCase();
  const filteredTransactions = (transactionsData || []).filter(
    (transaction) => {
      const matchesSearch =
        search === '' ||
        (transaction.orderNo ?? '').toLowerCase().includes(searchTerm) ||
        (transaction.status ?? '').toLowerCase().includes(searchTerm) ||
        (transaction.description ?? '').toLowerCase().includes(searchTerm) ||
        !!TRANSACTION_TYPES[
          transaction.type as keyof typeof TRANSACTION_TYPES
        ]?.label
          .toLowerCase()
          .includes(searchTerm);

      const matchesSort =
        sort.name === 'All' ||
        (sort.name === 'Pending' && transaction.status === 'PENDING') ||
        (sort.name === 'Success' && transaction.status === 'SUCCESS') ||
        (sort.name === 'Failed' && transaction.status === 'FAILED') ||
        (sort.name === 'Cancelled' && transaction.status === 'CANCELLED');

      return matchesSearch && matchesSort;
    },
  );

  // Calculate pagination info
  const totalPages = Math.ceil((total || 0) / pageSize);
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;

  // Handle pagination
  const handleNextPage = () => {
    if (hasNextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (hasPrevPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div className='card'>
      <div className='flex items-center max-md:h-12'>
        <div className='pl-5 text-h6 max-lg:pl-3 max-md:mr-auto'>{t('wallet.history')}</div>
        <Search
          className='w-70 ml-6 mr-auto max-lg:w-64 max-lg:ml-4 max-md:hidden'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder='Search...'
          isGray
        />
        {/* Always show tabs/dropdown for filtering */}
        <>
          <Tabs
            className='max-md:hidden'
            items={sortOptions}
            value={sort}
            setValue={setSort}
          />
          <Dropdown
            className='hidden max-md:block'
            items={sortOptions}
            value={sort}
            setValue={setSort}
          />
        </>
      </div>
      <div className='px-5 pb-4 max-lg:px-3'>
        <p className='text-sm text-t-secondary'>
          {t('wallet.history_description')}
        </p>
      </div>
      {isLoading ? (
        <div className='flex justify-center items-center py-8'>
          <Spinner />
        </div>
      ) : filteredTransactions.length === 0 ? (
        <NoFound title={t('wallet.no_transactions')} />
      ) : (
        <div className='p-1 pt-6 max-md:pt-3 max-lg:px-0 overflow-hidden w-full'>
          <div className='overflow-x-auto w-full'>
            <Table
              cellsThead={tableHead.map((head) => (
                <th
                  className={cn('!h-12.5 last:text-left whitespace-nowrap')}
                  key={head}
                >
                  {head}
                </th>
              ))}
              isMobileVisibleTHead
            >
              {filteredTransactions.map((transaction) => (
                <TableRow key={transaction.id}>
                  <td>{transaction?.id || '-'}</td>
                  <td className='max-md:text-caption w-32 whitespace-nowrap'>
                    {formatDate(
                      transaction?.transactionTime ||
                        transaction?.createdAt ||
                        '',
                    )}
                  </td>
                  <td>
                    <div
                      className={`label whitespace-nowrap capitalize ${
                        STATUS_COLORS[
                          transaction.status as keyof typeof STATUS_COLORS
                        ] || 'label-gray'
                      }`}
                    >
                      {(transaction.status ?? '-').toLowerCase()}
                    </div>
                  </td>
                  <td>
                    <div className='inline-flex items-center gap-1'>
                      <div
                        className={`label whitespace-nowrap ${
                          TRANSACTION_TYPES[
                            transaction.type as keyof typeof TRANSACTION_TYPES
                          ]?.color || 'label-gray'
                        }`}
                      >
                        {TRANSACTION_TYPES[
                          transaction.type as keyof typeof TRANSACTION_TYPES
                        ]?.label || transaction.type || '-'}
                      </div>
                    </div>
                  </td>
                  <td className='font-mono text-sm whitespace-nowrap'>
                    <div className='flex items-center gap-2'>
                      <span>{shortenAddress(transaction.orderNo)}</span>
                      <button
                        onClick={() => copyToClipboard(transaction.orderNo)}
                        className='p-1 hover:bg-gray-100 rounded transition-colors'
                        title='Copy order number'
                      >
                        <Icon
                          name='copy'
                          className='!size-4 fill-t-secondary hover:fill-t-primary'
                        />
                      </button>
                    </div>
                  </td>
                  <td className='text-right text-sub-title-2 max-md:text-body-2 max-md:font-normal whitespace-nowrap'>
                    <CurrencyFormat value={getTransactionAmount(transaction)} />
                  </td>
                  <td className='text-sm whitespace-nowrap'>
                    {transaction.description || '-'}
                  </td>
                </TableRow>
              ))}
            </Table>
          </div>
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className='flex items-center justify-between mt-6 px-6 max-lg:flex-col max-lg:gap-4'>
              <div className='text-sm text-t-secondary'>
                Showing page {currentPage} of {totalPages} ({total} total
                records)
              </div>
              <div className='flex items-center gap-2'>
                <button
                  onClick={handlePrevPage}
                  disabled={!hasPrevPage}
                  className='flex items-center justify-center w-10 h-10 rounded-lg border border-s-stroke2 text-sm font-medium transition-colors hover:bg-b-surface2 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  <Icon name='arrow' className='rotate-180 fill-t-secondary' />
                </button>

                {/* Page numbers */}
                <div className='flex items-center gap-1'>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`flex items-center justify-center w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === pageNum
                            ? 'bg-b-primary text-t-primary border border-s-focus'
                            : 'border border-s-stroke2 hover:bg-b-surface2'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={handleNextPage}
                  disabled={!hasNextPage}
                  className='flex items-center justify-center w-10 h-10 rounded-lg border border-s-stroke2 text-sm font-medium transition-colors hover:bg-b-surface2 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  <Icon name='arrow' className='fill-t-secondary' />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TransactionsHistory;
